import consentGate from '../platform/shopify/consentGate';
import storageKeys from './storageKeys';
import isLocalDevHost from './isLocalDevHost';

const COOKIE_UUID = storageKeys.CARTSEE_UID;

const PLATFORM_CONTEXTS = {
  'carttrack.js': {
    family: 'shopify',
    entryNames: ['cartAnimate', 'cartAnimateTest']
  },
  'carttrack-shopyy.js': {
    family: 'shopyy',
    entryNames: ['cart-animate-shopyy', 'cart-animate-shopyy-test']
  },
  'carttrack-shopline.js': {
    family: 'shopline',
    entryNames: ['cart-animate-shopline', 'cart-animate-shopline-test']
  },
  'carttrack-shoplazza-test.js': {
    family: 'shoplazza',
    entryNames: ['cart-animate-shoplazza', 'cart-animate-shoplazza-test']
  },
  'carttrack-xike.js': {
    family: 'xike',
    entryNames: ['cart-animate-xike', 'cart-animate-xike-test']
  },
  'carttrack-zhuomai.js': {
    family: 'common',
    entryNames: ['cart-animate-common', 'cart-animate-common-test']
  },
  'carttrack-common.js': {
    family: 'common',
    entryNames: ['cart-animate-common', 'cart-animate-common-test']
  },
  'carttrack-simple.js': {
    family: 'simple',
    entryNames: ['cart-animate-simple', 'cart-animate-simple-test']
  },
  'carttrack-woocommerce.js': {
    family: 'woocommerce',
    entryNames: ['cart-animate-woocommerce', 'cart-animate-woocommerce-test']
  }
};

export function getCookie(name) {
  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
}

export function setCookie(name, value, days = null, path = '/', domain = null, secure = false) {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }
  cookieString += `; path=${path}`;
  if (domain) {
    cookieString += `; domain=${domain}`;
  }
  if (secure) {
    cookieString += '; secure';
  }
  document.cookie = cookieString;
}

function getTopLevelDomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (/^\d+(\.\d+){3}$/.test(hostname)) {
    return hostname;
  }

  return parts.slice(-2).join('.');
}

export function getLocal(key) {
  if (typeof window === 'undefined') return null;
  const storage = window.localStorage;
  return storage ? storage.getItem(key) : null;
}

export function setLocal(key, value) {
  if (typeof window === 'undefined') return;
  const storage = window.localStorage;
  if (storage) storage.setItem(key, value);
}

function uuidv4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

function currentPlatformContext() {
  const currentScript = document.currentScript;
  const source = currentScript?.src || currentScript?.getAttribute('data-src') || '';
  const match = source.match(/(?:^|\/)(carttrack(?:-shopyy|-shopline|-shoplazza|-xike|-zhuomai|-common|-simple|-woocommerce)?\.js)(?:[?#]|$)/);
  return match ? PLATFORM_CONTEXTS[match[1]] : undefined;
}

function getcartAnimate(entryNames) {
  let str = '';
  let cartAnimate = document.getElementsByTagName('script');
  for (let i = 0; i < cartAnimate.length; i++) {
    if (entryNames.some(entryName => cartAnimate[i].src?.indexOf(entryName + '.js') > 0)) {
      str = cartAnimate[i].src;
    }
    if (
      entryNames.some(
        entryName => cartAnimate[i]?.getAttribute('data-src')?.indexOf(entryName + '.js') > 0
      )
    ) {
      str = cartAnimate[i].getAttribute('data-src');
    }
  }
  let id = str.match(new RegExp('id=([^&]+)')) || [];
  return id[1] || (isLocalDevHost() ? 'l2y7m6c5' : '');
}

const platformContext = currentPlatformContext();

export const companyUUID = platformContext ? getcartAnimate(platformContext.entryNames) : '';

function getShopifyUid() {
  if (!consentGate.isTrackingAllowed()) return '';

  const shopifyId = getCookie('_shopify_y');
  if (!shopifyId) {
    const companyUUID = getcartAnimate(platformContext.entryNames);
    const cartseeUUID = getLocal(companyUUID);
    if (!cartseeUUID) {
      const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
      setLocal(companyUUID, uuid);
      setLocal(COOKIE_UUID, uuid);
    }
  }
  return getLocal(COOKIE_UUID) || getCookie('_shopify_y') || '';
}

function getShoplineUid() {
  const companyUUID = getcartAnimate(platformContext.entryNames);
  let cartseeUUID = getCookie(companyUUID) || getCookie('cartsee_uid');
  if (!cartseeUUID || cartseeUUID === 'undefined') {
    cartseeUUID = JSON.parse(getCookie('n_sess') || '{}').session_id;
    setCookie('cartsee_uid', cartseeUUID, 365, '/');
  }

  if (!getCookie(companyUUID) && cartseeUUID) {
    setCookie(companyUUID, cartseeUUID, 365, '/', getTopLevelDomain());
  }
  if (getCookie(companyUUID) !== getCookie(COOKIE_UUID)) {
    setCookie(COOKIE_UUID, cartseeUUID, 366, '/');
  }

  if (!cartseeUUID) {
    const lsCartseeUUID = getLocal(companyUUID);
    if (!lsCartseeUUID) {
      const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
      setLocal(companyUUID, uuid);
      setLocal(COOKIE_UUID, uuid);
    }
  }

  return (
    getLocal(COOKIE_UUID) ||
    cartseeUUID ||
    JSON.parse(getCookie('n_sess') || '{}').session_id
  );
}

function getShoplazzaUid() {
  const userId = getCookie('_c_id');
  if (!userId) {
    const companyUUID = getcartAnimate(platformContext.entryNames);
    const cartseeUUID = getLocal(companyUUID);
    if (!cartseeUUID) {
      const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
      setLocal(companyUUID, uuid);
      setLocal(COOKIE_UUID, uuid);
    }
  }
  return getLocal(COOKIE_UUID) || getCookie('_c_id') || '';
}

function getSimpleUid() {
  const cookie = getCookie('cartx-cookie');
  if (cookie) {
    return cookie;
  }

  const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
  setCookie('cartx-cookie', uuid);
  return uuid;
}

export function getShopifyUserId() {
  if (!platformContext) return '';

  switch (platformContext.family) {
    case 'shopify':
      return getShopifyUid();
    case 'shopyy':
      return window?.oemcart_global_unique_id || '';
    case 'shopline':
      return getShoplineUid();
    case 'shoplazza':
      return getShoplazzaUid();
    case 'xike':
      return getCookie('mirage_cid') || '';
    case 'common':
      return getCookie('cartx-cookie') || '';
    case 'simple':
      return getSimpleUid();
    case 'woocommerce':
      return getCookie('cartsee_uid') || '';
  }
}
